import { Component, model } from "@angular/core";
import { finalize } from "rxjs";
import { Crop } from "../../models/crop.model";
import { Client } from "../../models/client.model";
import {
  DataService,
  ManagerEtDateValuesResponse,
} from "../../services/api/data/data.service";
import { ModalService } from "../../services/modal/modal.service";
import { AuthService } from "../../services/api/auth/auth.service";
import { FieldViewManagerComponent } from "../../modals/field-view-manager/field-view-manager.component";
import { MapComponent } from "../map/map.component";
import { LoadingService } from "../../services/loading/loading";
import { FieldSelectionUpdateComponent } from "../../modals/field-selection-update/field-selection-update.component";
import { FieldFeatureCollection } from "../../models/fields.model";

@Component({
  selector: "app-field-claim-manager",
  standalone: true,
  imports: [
    MapComponent,
    FieldSelectionUpdateComponent,
    FieldViewManagerComponent,
  ],
  templateUrl: "./field-claim-manager.component.html",
  styleUrl: "./field-claim-manager.component.scss",
  host: {
    class: "flex flex-col h-full overflow-hidden",
  },
})
export class FieldClaimManagerComponent {
  crops?: Crop[];
  cropsNoFallow?: Crop[];
  clients!: Client[];
  fields?: FieldFeatureCollection;
  clientId = model<string>("17"); // ! Default to cawelo
  properties?: any;
  createdOrUpdateProperties?: any;
  isDarkMode = false;
  isUpdateZoomLevel = true;
  etHistoryLoading = false;
  resolvedEtDates: string[] = [];
  failedEtDates: string[] = [];
  private fieldsRequestSeq = 0;
  private fieldsReady = false;
  private pendingEtDate?: string;
  private currentEtDate?: string;

  constructor(
    private dataService: DataService,
    private modalService: ModalService,
    private authService: AuthService,
    private loadingService: LoadingService,
  ) {}

  onCreateUpdateCropRecords(fieldId?: number) {
    // patch just the edited field in place instead of re-downloading the
    // whole district (one full /fields call). Falls back to a full refetch.
    if (fieldId != null && this.fields) {
      this.dataService
        .getFieldFeature(fieldId, this.clientId(), false)
        .subscribe({
          next: (response) => this.patchFieldFeatures(fieldId, response),
          error: () => {
            if (this.clientId()) this.fetchFields(this.clientId(), false);
          },
        });
      return;
    }
    if (this.clientId()) {
      this.fetchFields(this.clientId(), false);
    }
  }

  /**
   * Replace a field's feature(s) in the in-memory collection (a field can
   * be claimed by multiple growers in a manager's district, so this handles 0..n
   * features) and reassign `fields` so the map redraws just that field — no
   * full-district refetch. Then re-merge ET for the current slider date from the
   * cache so the patched field re-colors. Falls back to a full refetch if needed.
   */
  private patchFieldFeatures(fieldId: number, response: any): void {
    if (!this.fields) {
      if (this.clientId()) this.fetchFields(this.clientId(), false);
      return;
    }
    const incoming: any[] = response?.features ?? [];
    const features = (this.fields.features as any[]).filter(
      (f) => Number(f?.properties?.id) !== Number(fieldId),
    );
    features.push(...incoming);
    this.isUpdateZoomLevel = false;
    // New object reference so the map's ngOnChanges fires and redraws.
    this.fields = { ...this.fields, features } as FieldFeatureCollection;
    // Re-merge ET for the currently selected date so the patched field re-colors.
    if (this.currentEtDate) {
      this.loadEtDate(this.currentEtDate);
    }
  }

  private openFieldSelectionUpdateModal(): void {
    queueMicrotask(() => this.modalService.turnFieldSelectionUpdateModalOn());
  }

  selectUpdateModalShow(properties: string) {
    const propertyObj = JSON.parse(properties);
    if (propertyObj.owner) {
      // View Mode, not Add
      if (JSON.stringify(this.properties) === properties) {
        this.modalService.toggleFieldViewManagerModal();
      } else {
        this.properties = JSON.parse(properties);
      }
    } else {
      const propertyObj = JSON.parse(properties);
      if (
        this.createdOrUpdateProperties &&
        this.createdOrUpdateProperties?.id === propertyObj.id &&
        this.createdOrUpdateProperties?.owner === propertyObj.owner // need to update after claiming => owner change
      ) {
        this.openFieldSelectionUpdateModal(); // ! RIGHT HERE put break point. It seems the properties is not updating like we expected due to the if condition
      } else {
        this.createdOrUpdateProperties = propertyObj;
        this.openFieldSelectionUpdateModal();
      }
    }
  }

  ngOnInit(): void {
    const token = this.authService.getToken();
    if (!token) return;
    const decode = this.authService.getDecodedToken();
    const clientId: string = decode["signup_client_id"]; // Cawelo as default
    if (clientId) {
      this.clientId.set(clientId);
    }
    // fetch all crops
    this.fetchCrops();
    // fetch all clients
    this.fetchClients();
    // fetch all field boundaries
    this.fetchFields(this.clientId());
    // Initialize dark mode based on the current state in localStorage
    this.isDarkMode = localStorage.getItem("darkMode") === JSON.stringify(true);
    // Listen for localStorage changes in this tab
    window.addEventListener("storage", this.onStorageChange.bind(this));
  }

  onStorageChange(event: StorageEvent) {}

  ngOnDestroy() {
    // Remove the event listener when the component is destroyed
    window.removeEventListener("storage", this.onStorageChange.bind(this));
  }

  // Getting all crops by calling dataService
  fetchCrops(): void {
    this.loadingService.start({
      token: "field-claim-manager-crops",
      message: "Loading crops",
    });
    this.dataService
      .getAllCrops()
      .pipe(
        finalize(() => this.loadingService.stop("field-claim-manager-crops")),
      )
      .subscribe({
        next: (crops: Crop[]) => {
          this.crops = crops;
          this.cropsNoFallow = this.crops.filter(
            (crop) => crop.crop_type !== "Fallow",
          );
        },
        error: (error: any) => {
          console.error("Error fetching crops data", error);
        },
      });
  }

  // Getting all clients by calling dataService
  fetchClients(): void {
    this.loadingService.start({
      token: "field-claim-manager-clients",
      message: "Loading clients",
    });
    this.dataService
      .getAllClients()
      .pipe(
        finalize(() => this.loadingService.stop("field-claim-manager-clients")),
      )
      .subscribe({
        next: (clients: Client[]) => {
          this.clients = clients;
        },
        error: (error: any) => {
          console.error("Error fetching GeoJSON data", error);
        },
      });
  }

  // Getting all field boundaries by calling dataService
  fetchFields(clientId?: string, updateZoomLevel = true): void {
    clientId = !clientId ? "" : clientId;
    const requestSeq = ++this.fieldsRequestSeq;
    this.fieldsReady = false;
    this.resolvedEtDates = [];
    this.failedEtDates = [];
    this.isUpdateZoomLevel = updateZoomLevel;
    this.etHistoryLoading = false;
    this.loadingService.start({
      token: "field-claim-manager-fields",
      message: "Loading fields",
    });
    this.dataService
      .getAllFields(clientId, false)
      .pipe(
        finalize(() => this.loadingService.stop("field-claim-manager-fields")),
      )
      .subscribe({
        next: (fields: FieldFeatureCollection) => {
          if (requestSeq !== this.fieldsRequestSeq) return;
          this.fields = fields;
          this.fieldsReady = true;
          if (this.pendingEtDate) {
            const date = this.pendingEtDate;
            this.pendingEtDate = undefined;
            this.loadEtDate(date);
          }
        },
        error: (error: any) => {
          if (requestSeq === this.fieldsRequestSeq) {
            this.etHistoryLoading = false;
          }
          console.error("Error fetching GeoJSON data", error);
        },
      });
  }

  loadEtDate(etDate: string): void {
    // The initial slider request can arrive before the field boundaries.
    // Defer it so its response cannot be discarded and marked resolved.
    if (!this.fieldsReady) {
      this.pendingEtDate = etDate;
      return;
    }
    this.failedEtDates = this.failedEtDates.filter((date) => date !== etDate);
    this.currentEtDate = etDate; // IM-613: remember for post-edit ET refresh
    const requestSeq = this.fieldsRequestSeq;
    this.etHistoryLoading = true;

    this.dataService.getManagerEtValuesForDate(etDate).subscribe({
      next: (response: ManagerEtDateValuesResponse) => {
        if (requestSeq !== this.fieldsRequestSeq) return;
        this.fields = this.mergeEtDateIntoFields(this.fields, response, etDate);
        this.resolvedEtDates = [...new Set([...this.resolvedEtDates, etDate])];
        this.isUpdateZoomLevel = false;
        this.etHistoryLoading = false;
      },
      error: (error: any) => {
        if (requestSeq === this.fieldsRequestSeq) {
          this.failedEtDates = [...new Set([...this.failedEtDates, etDate])];
          this.resolvedEtDates = [
            ...new Set([...this.resolvedEtDates, etDate]),
          ];
          this.etHistoryLoading = false;
        }
        console.error("Error loading GeoJSON ET date", error);
      },
    });
  }

  private mergeEtDateIntoFields(
    currentFields: FieldFeatureCollection | undefined,
    incomingEtValues: ManagerEtDateValuesResponse,
    etDate: string,
  ): FieldFeatureCollection | undefined {
    if (!currentFields) return currentFields;

    return {
      ...currentFields,
      features: (currentFields.features as any[]).map((feature) => {
        const incomingEtValue =
          incomingEtValues.values[String(feature.properties.id)];

        if (!incomingEtValue || !feature.properties?.claimed_details) {
          return feature;
        }

        return {
          ...feature,
          properties: {
            ...feature.properties,
            claimed_details: {
              ...feature.properties.claimed_details,
              userFieldEtvalues: {
                ...(feature.properties.claimed_details.userFieldEtvalues ?? {}),
                [etDate]: incomingEtValue,
              },
            },
          },
        };
      }),
    } as FieldFeatureCollection;
  }
}
